<template>
  <div class="statistic-dashboard">
    <!-- Lặp qua từng Layer được chọn -->
    <div v-for="layerId in layerIds" :key="layerId" class="layer-section mb-4">
      <!-- Tiêu đề Layer -->
      <h5 class="layer-title pb-2 mb-3 d-flex align-center">
        <v-icon color="primary" left small>mdi-map-marker</v-icon>
        <span>{{ layerMap[layerId] || 'Layer #' + layerId }}</span>
      </h5>

      <!-- Loading cấu hình -->
      <div v-if="loadingConfigs[layerId]" class="pa-5 text-center">
        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        <div class="mt-2 caption grey--text">Đang tải dữ liệu biểu đồ...</div>
      </div>

      <!-- Không có biểu đồ nào -->
      <v-alert v-else-if="!layerChartConfigs[layerId] || layerChartConfigs[layerId].length === 0" type="info" text
        dense>
        Không có biểu đồ nào cho lớp này
      </v-alert>

      <!-- Dạng Tab cho các Biểu đồ -->
      <v-card v-else outlined class="chart-tab-card overflow-hidden">
        <v-tabs v-model="activeTabMap[layerId]" background-color="grey lighten-4" color="primary" show-arrows
          active-class="font-weight-bold primary--text" dense>
          <v-tab v-for="chart in layerChartConfigs[layerId]" :key="chart.id" class="text-capitalize">
            <v-icon left small :color="chartTypeColor(chart.type)">{{ chartTypeIcon(chart.type) }}</v-icon>
            {{ chart.name }}
          </v-tab>
        </v-tabs>

        <v-divider></v-divider>

        <v-tabs-items v-model="activeTabMap[layerId]">
          <v-tab-item v-for="chart in layerChartConfigs[layerId]" :key="chart.id">
            <v-card-text class="pa-4">
              <!-- Lỗi truy vấn hoặc cấu hình không hợp lệ -->
              <v-alert v-if="chartDataMap[chart.id] && chartDataMap[chart.id].error" type="warning" outlined class="my-2">
                <div class="d-flex align-center">
                  <v-icon left color="warning">mdi-alert-circle-outline</v-icon>
                  <div>{{ chartDataMap[chart.id].error }}</div>
                </div>
              </v-alert>

              <!-- Canvas biểu đồ hiển thị trực tiếp (Default show full) -->
              <div v-else-if="chartDataMap[chart.id]" class="chart-wrapper" :style="chartWrapperStyle(chart.type)">
                <ChartJSComponent :type="chart.type" :labels="chartDataMap[chart.id].labels"
                  :datasets="chartDataMap[chart.id].datasets" :options="chartOptions(chart.type)"
                  style="height: 100%; width: 100%;" />
              </div>

              <!-- State chờ tải -->
              <div v-else class="text-center py-8">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <div class="mt-2 text-caption grey--text">Đang render dữ liệu...</div>
              </div>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </div>
</template>

<script>
import ChartJSComponent from '@components/chart/ChartJSComponent.vue'
import { sdk } from '@service/http/axios'

const COLOR_PALETTE = [
  '#4A90E2', '#50E3C2', '#B8E986', '#F5A623', '#D0021B',
  '#BD10E0', '#9013FE', '#417505', '#7ED321', '#F8E71C',
  '#4ECDC4', '#FF6B6B', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#20B2AA', '#FF7F50', '#87CEEB', '#98D8C8'
]

export default {
  name: 'StatisticDashboard',
  components: { ChartJSComponent },
  props: {
    layerIds: { type: Array, required: true },
    layerMap: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      layerChartConfigs: {},  // { layerId: [configs] }
      chartDataMap: {},       // { chartId: { labels, datasets } }
      loadingConfigs: {},     // { layerId: boolean }
      activeTabMap: {},       // { layerId: number }
    }
  },
  watch: {
    layerIds: {
      handler(newIds) {
        newIds.forEach(id => {
          if (!this.layerChartConfigs[id]) {
            this.fetchBatchChartData(id)
          }
        })
      },
      immediate: true,
    },
  },
  methods: {
    async fetchBatchChartData(layerId) {
      this.$set(this.loadingConfigs, layerId, true)
      try {
        // Gọi API Batch duy nhất để lấy toàn bộ Cấu hình + Data các biểu đồ của Layer
        const { data: res } = await sdk.request({
          url: `layer/${layerId}/chart-configs/batch-data`,
          method: 'get'
        })

        const configList = res.configs || []
        const rawBatchData = res.data || {}

        this.$set(this.layerChartConfigs, layerId, configList)
        if (this.activeTabMap[layerId] === undefined) {
          this.$set(this.activeTabMap, layerId, 0)
        }

        // Xử lý dữ liệu cho từng biểu đồ
        configList.forEach(chart => {
          const rawData = rawBatchData[chart.id]
          if (rawData && !rawData.error) {
            const processedData = this.processDataForChart(rawData)
            const count = processedData.labels ? processedData.labels.length : 0

            const truncatedLabels = (processedData.labels || []).map(label => {
              const str = String(label || '');
              return str.length > 16 ? str.substring(0, 16) + '...' : str;
            });

            const bgColors = this.getColors(count);
            if (processedData.labels && processedData.labels[count - 1] === 'Khác...') {
              bgColors[count - 1] = '#9E9E9E';
            }

            let dataset = {}
            const isPie = chart.type === 'pie' || chart.type === 'doughnut'
            const isLine = chart.type === 'line'

            if (isLine) {
              dataset = {
                label: processedData.datasets[0].label,
                data: processedData.datasets[0].data,
                borderColor: '#4A90E2',
                backgroundColor: 'rgba(74, 144, 226, 0.08)',
                borderWidth: 2.5,
                pointBackgroundColor: bgColors,
                pointBorderColor: '#ffffff',
                pointBorderWidth: 1.5,
                pointRadius: 4.5,
                pointHoverRadius: 7,
                fill: true,
                lineTension: 0.35,
              }
            } else if (isPie) {
              dataset = {
                label: processedData.datasets[0].label,
                data: processedData.datasets[0].data,
                backgroundColor: bgColors,
                borderColor: '#ffffff',
                borderWidth: 2,
              }
            } else {
              dataset = {
                label: processedData.datasets[0].label,
                data: processedData.datasets[0].data,
                backgroundColor: bgColors,
                borderColor: bgColors,
                borderWidth: 1,
              }
            }

            this.$set(this.chartDataMap, chart.id, {
              labels: truncatedLabels,
              datasets: [dataset],
            })
          } else if (rawData && rawData.error) {
            this.$set(this.chartDataMap, chart.id, { error: rawData.error })
          }
        })
      } catch (e) {
        this.$store.dispatch('notify/error', { message: 'Lỗi khi tải dữ liệu biểu đồ' })
      } finally {
        this.$set(this.loadingConfigs, layerId, false)
      }
    },

    chartWrapperStyle(type) {
      const isPie = type === 'pie' || type === 'doughnut'
      return {
        width: '100%',
        minHeight: isPie ? '420px' : '400px',
        height: isPie ? '420px' : '400px',
        position: 'relative',
        padding: '4px',
      }
    },

    chartOptions(type) {
      const isPie = type === 'pie' || type === 'doughnut'
      if (isPie) {
        return {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: true,
            position: 'right',
            labels: {
              boxWidth: 10,
              padding: 10,
              fontSize: 11,
              usePointStyle: true,
            }
          },
          layout: {
            padding: { top: 10, bottom: 10, left: 10, right: 10 }
          }
        }
      }
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
          labels: {
            boxWidth: 10,
            padding: 10,
            fontSize: 12,
            usePointStyle: true,
          }
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false,
            },
            ticks: {
              maxRotation: 40,
              minRotation: 0,
              fontSize: 11,
              fontColor: '#555',
            }
          }],
          yAxes: [{
            gridLines: {
              color: 'rgba(0,0,0,0.06)',
              zeroLineColor: 'rgba(0,0,0,0.12)',
            },
            ticks: {
              beginAtZero: true,
              fontSize: 11,
              fontColor: '#555',
            }
          }],
        },
      }
    },

    getColors(count) {
      return Array.from({ length: count }, (_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length])
    },

    processDataForChart(rawData) {
      const MAX_LABELS = 15;
      if (!rawData || !rawData.labels || rawData.labels.length <= MAX_LABELS) {
        return rawData;
      }

      let newLabels = rawData.labels.slice(0, MAX_LABELS);
      let newData = rawData.datasets[0].data.slice(0, MAX_LABELS);

      let othersSum = rawData.datasets[0].data
        .slice(MAX_LABELS)
        .reduce((a, b) => Number(a) + Number(b), 0);

      newLabels.push('Khác...');
      newData.push(othersSum);

      return {
        labels: newLabels,
        datasets: [{
          ...rawData.datasets[0],
          data: newData
        }]
      };
    },

    chartTypeLabel(type) {
      return { line: 'Đường', bar: 'Cột', pie: 'Tròn', doughnut: 'Vành khuyên' }[type] || type
    },
    chartTypeTag(type) {
      return { line: 'primary', bar: 'success', pie: 'warning', doughnut: 'error' }[type] || 'primary'
    },
    chartTypeIcon(type) {
      return { line: 'mdi-chart-line', bar: 'mdi-chart-bar', pie: 'mdi-chart-pie', doughnut: 'mdi-chart-donut' }[type] || 'mdi-chart-box'
    },
    chartTypeColor(type) {
      return { line: 'primary', bar: 'success', pie: 'warning', doughnut: 'error' }[type] || 'primary'
    },
  },
}
</script>

<style scoped>
.layer-title {
  border-bottom: 2px solid #1976D2;
}

.chart-tab-card {
  border-radius: 8px;
}
</style>
